import { useEffect, useMemo, useReducer, useRef } from 'react'
import type { ChatEvent, ChatSnapshot, ChatUpdate } from '../../api'
import { subscribeSharedSse, type SharedSseState } from '../../sharedSse'

/*
 * Chats of the user's outputs (docs/chat.md): GET /api/chat/events, shared by the tabs of the
 * browser like /api/events (sharedSse.ts: one stream, the leader posts to the others, a joining
 * tab gets the leader's items as a chat_snapshot). The store keeps the last KEEP items in arrival
 * order, deduped by id; chat_snapshot replaces them, chat_update removes some.
 *
 * Unread: every item gets a local arrival number; `seen` is the last number the streamer could
 * see (the Chats mode visible). Items of the first snapshot are history, never unread; a later
 * snapshot (the stream fell behind) counts only the items the store did not have.
 */

/** Items kept in the window (the center keeps 1,000 too). */
export const KEEP = 1000

export interface ChatItem {
  e: ChatEvent
  /** local arrival number */
  n: number
}

export interface ChatState {
  items: ChatItem[]
  /** last arrival number given */
  n: number
  /** last arrival number seen with Chats visible */
  seen: number
  cursor?: string
  /** a snapshot arrived: the store holds the chat's state */
  started: boolean
  conn: SharedSseState
}

export type ChatAction =
  | { t: 'snapshot'; snap: ChatSnapshot }
  | { t: 'chat'; e: ChatEvent; cursor?: string }
  | { t: 'update'; u: ChatUpdate; cursor?: string }
  | { t: 'seen' }
  | { t: 'conn'; conn: SharedSseState }

export const EMPTY_CHAT: ChatState = { items: [], n: 0, seen: 0, started: false, conn: 'connecting' }

const keep = (items: ChatItem[]) => (items.length > KEEP ? items.slice(items.length - KEEP) : items)

export function chatReduce(s: ChatState, a: ChatAction): ChatState {
  switch (a.t) {
    case 'conn':
      return s.conn === a.conn ? s : { ...s, conn: a.conn }
    case 'seen':
      return s.seen === s.n ? s : { ...s, seen: s.n }
    case 'snapshot': {
      const known = new Map(s.items.map((i) => [i.e.id, i.n]))
      const ids = new Set<string>()
      let n = s.n
      const items: ChatItem[] = []
      for (const e of a.snap.events) {
        if (!validEvent(e) || ids.has(e.id)) continue
        ids.add(e.id)
        items.push({ e, n: known.get(e.id) ?? ++n })
      }
      return { ...s, items: keep(items), n, seen: s.started ? s.seen : n, cursor: a.snap.cursor, started: true }
    }
    case 'chat': {
      if (!validEvent(a.e) || s.items.some((i) => i.e.id === a.e.id)) return a.cursor ? { ...s, cursor: a.cursor } : s
      const n = s.n + 1
      return { ...s, items: keep([...s.items, { e: a.e, n }]), n, cursor: a.cursor ?? s.cursor }
    }
    case 'update': {
      const u = a.u
      const drop = (e: ChatEvent): boolean => {
        if (u.op === 'delete') return !!u.ids?.includes(e.id)
        if (e.output_id !== u.output_id) return false
        if (u.op === 'clear') return true
        if (u.op === 'ban') return !!u.author_id && e.author?.id === u.author_id
        return false
      }
      const items = s.items.filter((i) => !drop(i.e))
      return { ...s, items: items.length === s.items.length ? s.items : items, cursor: a.cursor ?? s.cursor }
    }
  }
}

function validEvent(e: unknown): e is ChatEvent {
  const x = e as Partial<ChatEvent> | null
  return !!x && typeof x.id === 'string' && typeof x.output_id === 'string' && typeof x.text === 'string'
}

/** One SSE event of /api/chat/events as the reducer's action; null = not understood. */
export function chatAction(name: string, data: string, lastEventId?: string): ChatAction | null {
  let d: unknown
  try {
    d = JSON.parse(data)
  } catch {
    return null
  }
  if (!d || typeof d !== 'object') return null
  switch (name) {
    case 'chat_snapshot': {
      const snap = d as Partial<ChatSnapshot>
      return Array.isArray(snap.events) ? { t: 'snapshot', snap: { cursor: snap.cursor ?? lastEventId ?? '', events: snap.events } } : null
    }
    case 'chat':
      return { t: 'chat', e: d as ChatEvent, cursor: lastEventId }
    case 'chat_update':
      return typeof (d as ChatUpdate).op === 'string' ? { t: 'update', u: d as ChatUpdate, cursor: lastEventId } : null
  }
  return null
}

/** Items that count as unread: arrived after `seen`, not the center's own system lines. */
export function unreadOf(s: ChatState): { byOutput: Record<string, number>; total: number } {
  const byOutput: Record<string, number> = {}
  let total = 0
  for (const i of s.items) {
    if (i.n <= s.seen || i.e.kind === 'system') continue
    byOutput[i.e.output_id] = (byOutput[i.e.output_id] ?? 0) + 1
    total++
  }
  return { byOutput, total }
}

const CHAT_PATH = '/api/chat/events'
const CHAT_EVENTS = ['chat_snapshot', 'chat', 'chat_update'] as const

/**
 * The chats of the signed-in user while `enabled` (an output reads chat and the Live page is
 * open); `visible`: the Chats mode is on screen, so what arrives is read.
 */
export function useChat(enabled: boolean, visible: boolean) {
  const [state, dispatch] = useReducer(chatReduce, EMPTY_CHAT)
  const ref = useRef(state)
  ref.current = state

  useEffect(() => {
    if (!enabled) return
    return subscribeSharedSse({
      path: CHAT_PATH,
      key: 'strevix-live-chat',
      events: CHAT_EVENTS,
      snapshotEvent: 'chat_snapshot',
      onEvent: (e) => {
        const a = chatAction(e.name, e.data, e.lastEventId)
        if (a) dispatch(a)
      },
      onState: (conn) => dispatch({ t: 'conn', conn }),
      snapshot: () => {
        const s = ref.current
        if (!s.started) return null
        const snap: ChatSnapshot = { cursor: s.cursor ?? '', events: s.items.map((i) => i.e) }
        return { name: 'chat_snapshot', data: JSON.stringify(snap), lastEventId: s.cursor }
      },
    })
  }, [enabled])

  useEffect(() => {
    if (visible && state.n > state.seen) dispatch({ t: 'seen' })
  }, [visible, state.n, state.seen])

  const unread = useMemo(() => unreadOf(state), [state])
  return { ...state, unread: visible ? { byOutput: {}, total: 0 } : unread, active: enabled }
}

export type ChatStore = ReturnType<typeof useChat>
